import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Tabs, Tab, Paper } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { getMigrationLatest } from '../../services/apiService';
import MigrationDashboardTab from './MigrationDashboardTab';
import MigrationTypeTab from './MigrationTypeTab';
import AzureMigrationStatisticsTab from './AzureMigrationStatisticsTab';
import AzureMigrationPerformanceView from './AzureMigrationPerformanceView';
import { onMigrationDataUpdated } from './migrationRefreshEvents';

const TAB_NAMES = ['dashboard', 'azure', 'azure-stats', 'azure-perf', 'ciam', 'isheet'];

/**
 * MigrationsView - Platform & Ops migrations: Dashboard, Azure, Azure statistics, CIAM, ISheet
 */
const MigrationsView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') || 'dashboard';
  const tabIndex = TAB_NAMES.includes(tabParam) ? TAB_NAMES.indexOf(tabParam) : 0;
  const [activeTab, setActiveTab] = useState(tabIndex);
  const [lastSyncDate, setLastSyncDate] = useState(null);

  useEffect(() => {
    const idx = TAB_NAMES.includes(tabParam) ? TAB_NAMES.indexOf(tabParam) : 0;
    setActiveTab(idx);
  }, [tabParam]);

  const refreshLastSync = useCallback(() => {
    let cancelled = false;
    getMigrationLatest()
      .then((res) => {
        if (!cancelled && res?.success && res?.data?.uploadedAt) setLastSyncDate(res.data.uploadedAt);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => refreshLastSync(), [refreshLastSync]);

  useEffect(() => onMigrationDataUpdated((detail) => {
    if (detail?.uploadedAt) {
      setLastSyncDate(detail.uploadedAt);
    } else {
      refreshLastSync();
    }
  }), [refreshLastSync]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const next = new URLSearchParams(searchParams);
    next.set('tab', TAB_NAMES[newValue]);
    setSearchParams(next);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ color: '#1D4434' }}>
            Migrations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Azure Cloud migration tracking: CIAM Migration and Backend ISheet Modernization
          </Typography>
        </Box>
        {lastSyncDate && (
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Last synced: {format(new Date(lastSyncDate), 'd MMM yyyy, HH:mm')}
          </Typography>
        )}
      </Box>

      <Box sx={{ mb: 3 }} />

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '1rem'
            }
          }}
        >
          <Tab label="Dashboard" />
          <Tab label="Azure Migration" />
          <Tab label="Azure Migration Statistics" />
          <Tab label="Azure Migration Performance" />
          <Tab label="CIAM Migration" />
          <Tab label="Backend ISheet Modernization Migration" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && <MigrationDashboardTab />}
          {activeTab === 1 && <MigrationTypeTab type="azure" title="Azure Migration" />}
          {activeTab === 2 && <AzureMigrationStatisticsTab />}
          {activeTab === 3 && <AzureMigrationPerformanceView />}
          {activeTab === 4 && <MigrationTypeTab type="ciam" title="CIAM Migration" />}
          {activeTab === 5 && <MigrationTypeTab type="isheet" title="Backend ISheet Modernization Migration" />}
        </Box>
      </Paper>
    </Box>
  );
};

export default MigrationsView;
